import * as React from 'react';
import { useState, useEffect } from 'react';
import { format } from 'date-fns';

// Types
import { BursaryRequest } from '@/types/organization/finances';

// Icons
import { HandCoins } from "lucide-react";
import { Eye, EyeOff, Check, X } from "lucide-react";

// Hooks
// import { useBursarySystemFlag } from '@/hooks/common/useFeatureFlag';

// Common Components
import { DisabledFeatureMessage } from "@/components/common/DisabledFeatureMessage";

// Shadcn Components
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

// Utility function to format currency
const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
    }).format(amount / 100);
};

interface BursaryRequestTableProps {
    requests: BursaryRequest[];
    isLoading: boolean;
    error: string | null;
    showName: { [key: string]: boolean };
    showModalName: { [key: string]: boolean };
    showModalEmail: { [key: string]: boolean };
    showBursaryModal: boolean;
    selectedBursary: BursaryRequest | null;
    onToggleNameVisibility: (uuid: string) => void;
    onToggleModalNameVisibility: (uuid: string) => void;
    onToggleModalEmailVisibility: (uuid: string) => void;
    onViewDetails: (request: BursaryRequest) => void;
    onAccept: (uuid: string) => void;
    onReject: (uuid: string) => void;
    onCloseModal: () => void;
    onError: (error: string | null) => void;
}

const BursaryRequestTable: React.FC<BursaryRequestTableProps> = ({
    requests,
    isLoading,
    error,
    showName,
    showModalName,
    showModalEmail,
    showBursaryModal,
    selectedBursary,
    onToggleNameVisibility,
    onToggleModalNameVisibility,
    onToggleModalEmailVisibility,
    onViewDetails,
    onAccept,
    onReject,
    onCloseModal,
    onError
}) => {
    const [localStatus, setLocalStatus] = useState<string | null>(null);
    // const { isEnabled: bursarySystemEnabled, loading: bursarySystemLoading } = useBursarySystemFlag();

    const handleAccept = async (uuid: string) => {
        try {
            await onAccept(uuid);
            // Only update status if there's no error
            if (!error) {
                setLocalStatus('approved');
            }
        } catch {
            // Let the parent component handle the error
        }
    };

    const handleReject = async (uuid: string) => {
        try {
            await onReject(uuid);
            // Only update status if there's no error
            if (!error) {
                setLocalStatus('rejected');
            }
        } catch {
            // Let the parent component handle the error
        }
    };

    const handleCloseModal = () => {
        onError(null); // Clear the error
        onCloseModal();
    };

    // Reset local status when modal closes or when there's an error
    useEffect(() => {
        if (!showBursaryModal || error) {
            setLocalStatus(null);
        }
    }, [showBursaryModal, error]);

    if (isLoading) {
        return (
            <div className="hidden md:block w-full">
                <div className="border rounded-lg overflow-hidden w-full">
                    <div className="min-h-[500px] max-h-[500px] overflow-auto w-full bg-gray-50">
                        <div className="grid w-full bg-white" style={{ 
                            gridTemplateColumns: 'minmax(80px, 1fr) minmax(70px, 1fr) minmax(60px, 0.8fr) minmax(60px, 0.7fr) minmax(60px, 0.8fr) minmax(70px, 0.7fr)'
                        }}>
                            {/* Header */}
                            <div className="sticky top-0 bg-black z-10 border-b py-2 px-4 font-medium text-white">Requester</div>
                            <div className="sticky top-0 bg-black z-10 border-b py-2 px-4 font-medium text-white text-center">Event</div>
                            <div className="sticky top-0 bg-black z-10 border-b py-2 px-4 font-medium text-white text-center">Amount</div>
                            <div className="sticky top-0 bg-black z-10 border-b py-2 px-4 font-medium text-white text-center">Date</div>
                            <div className="sticky top-0 bg-black z-10 border-b py-2 px-4 font-medium text-white text-center">Status</div>
                            <div className="sticky top-0 bg-black z-10 border-b py-2 px-4 font-medium text-white text-center">Actions</div>

                            {/* Loading Skeletons */}
                            {[...Array(12)].map((_, index) => (
                                <React.Fragment key={index}>
                                    <div className="border-b py-3 px-4">
                                        <div className="min-w-0 space-y-1">
                                            <div className="h-4 bg-muted rounded animate-pulse" />
                                            <div className="h-3 bg-muted rounded animate-pulse w-3/4" />
                                        </div>
                                    </div>
                                    <div className="border-b py-3 px-4 flex items-center">
                                        <div className="h-4 bg-muted rounded animate-pulse w-full" />
                                    </div>
                                    <div className="border-b py-3 px-4 font-medium text-green-600 whitespace-nowrap text-sm flex items-center justify-center">
                                        <div className="h-4 bg-muted rounded animate-pulse w-20" />
                                    </div>
                                    <div className="border-b py-3 px-4 whitespace-nowrap text-sm flex items-center justify-center">
                                        <div className="h-4 bg-muted rounded animate-pulse w-24" />
                                    </div>
                                    <div className="border-b py-3 px-4 flex items-center justify-center">
                                        <div className="h-5 bg-muted rounded-full animate-pulse w-20" />
                                    </div>
                                    <div className="border-b py-3 px-4 flex items-center justify-center gap-2">
                                        <div className="h-8 bg-muted rounded animate-pulse w-20" />
                                        <div className="h-8 bg-muted rounded animate-pulse w-20" />
                                    </div>
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // if (!bursarySystemEnabled) {
    //     return (
    //         <div className="text-center py-12">
    //             <HandCoins className="mx-auto h-12 w-12 text-muted-foreground" />
    //             <h3 className="mt-4 text-lg font-semibold">Bursary System Temporarily Disabled</h3>
    //             <div className="mt-4 max-w-md mx-auto">
    //                 <DisabledFeatureMessage 
    //                     featureName="TriEvents bursary system"
    //                     message="The bursary system is temporarily disabled. This affects all events on TriEvents, not just this specific event."
    //                 />
    //             </div>
    //             <p className="mt-4 text-sm text-muted-foreground">
    //                 Bursary request management is currently unavailable.
    //             </p>
    //         </div>
    //     );
    // }

    if (requests.length === 0) {
        return (
            <div className="text-center py-12">
                <HandCoins className="mx-auto h-12 w-12 text-muted-foreground" />
                <h3 className="mt-4 text-lg font-semibold">No bursary requests found</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                    There are no bursary requests to display at this time.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Mobile View */}
            <div className="md:hidden space-y-4">
                {isLoading ? (
                    [...Array(3)].map((_, i) => (
                        <Card key={i} className="p-4">
                            <div className="space-y-3">
                                <Skeleton className="h-5 w-3/4" />
                                <Skeleton className="h-4 w-1/2" />
                                <Skeleton className="h-4 w-1/3" />
                            </div>
                        </Card>
                    ))
                ) : (
                    requests.map((request) => (
                        <Card key={request.uuid} className="p-4">
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2">
                                            <TooltipProvider>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <p className="font-medium truncate text-sm">
                                                            {showName[request.uuid] ? request.requester_name : 'Hidden'}
                                                        </p>
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                        {showName[request.uuid] ? request.requester_name : 'Hidden'}
                                                    </TooltipContent>
                                                </Tooltip>
                                            </TooltipProvider>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => onToggleNameVisibility(request.uuid)}
                                                className="h-6 w-6"
                                            >
                                                {showName[request.uuid] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                            </Button>
                                        </div>
                                        <TooltipProvider>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <p className="text-xs text-muted-foreground truncate">
                                                        {showName[request.uuid] ? request.requester_email : 'Hidden'}
                                                    </p>
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    {showName[request.uuid] ? request.requester_email : 'Hidden'}
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    </div>
                                    <Badge
                                        variant="outline"
                                        className={cn(
                                            "rounded-full text-xs whitespace-nowrap w-[85px] justify-center",
                                            request.status === 'pending' && "bg-yellow-100 text-yellow-800 border-yellow-200",
                                            request.status === 'approved' && "bg-green-100 text-green-800 border-green-200",
                                            request.status === 'rejected' && "bg-red-100 text-red-800 border-red-200"
                                        )}
                                    >
                                        {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                                    </Badge>
                                </div>
                                <div className="space-y-2">
                                    <div className="flex items-center gap-2 text-sm">
                                        <span className="text-muted-foreground w-16">Event:</span>
                                        <TooltipProvider>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <p className="truncate">{request.event?.title || 'N/A'}</p>
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    {request.event?.title || 'N/A'}
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm">
                                        <span className="text-muted-foreground w-16">Amount:</span>
                                        <span className="font-medium text-green-600">{formatCurrency(request.amount)}</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm">
                                        <span className="text-muted-foreground w-16">Date:</span>
                                        <span>{format(new Date(request.updatedAt), 'MMM d, yyyy')}</span>
                                    </div>
                                </div>
                                <div className="pt-2">
                                    <Button
                                        variant="default"
                                        size="sm"
                                        onClick={() => onViewDetails(request)}
                                        className="w-full transition-transform hover:scale-105"
                                    >
                                        View Details
                                    </Button>
                                </div>
                            </div>
                        </Card>
                    ))
                )}
            </div>

            {/* Desktop View */}
            <div className="hidden md:block w-full">
                <div className="border rounded-lg overflow-hidden w-full">
                    <div className="min-h-[500px] max-h-[500px] overflow-auto w-full bg-gray-50">
                        <div className="grid w-full bg-white" style={{ 
                            gridTemplateColumns: 'minmax(80px, 1fr) minmax(70px, 1fr) minmax(60px, 0.8fr) minmax(60px, 0.7fr) minmax(60px, 0.8fr) minmax(70px, 0.7fr)'
                        }}>
                            {/* Header */}
                            <div className="sticky top-0 bg-black z-10 border-b py-2 px-4 font-medium text-white">Requester</div>
                            <div className="sticky top-0 bg-black z-10 border-b py-2 px-4 font-medium text-white text-center">Event</div>
                            <div className="sticky top-0 bg-black z-10 border-b py-2 px-4 font-medium text-white text-center">Amount</div>
                            <div className="sticky top-0 bg-black z-10 border-b py-2 px-4 font-medium text-white text-center">Date</div>
                            <div className="sticky top-0 bg-black z-10 border-b py-2 px-4 font-medium text-white text-center">Status</div>
                            <div className="sticky top-0 bg-black z-10 border-b py-2 px-4 font-medium text-white text-center">Actions</div>

                            {/* Loading State */}
                            {isLoading && [...Array(3)].map((_, i) => (
                                <React.Fragment key={i}>
                                    <div className="border-b py-2 px-4">
                                        <Skeleton className="h-5 w-full" />
                                    </div>
                                    <div className="border-b py-2 px-2">
                                        <Skeleton className="h-5 w-full" />
                                    </div>
                                    <div className="border-b py-2 px-2">
                                        <Skeleton className="h-5 w-full" />
                                    </div>
                                    <div className="border-b py-2 px-2">
                                        <Skeleton className="h-5 w-full" />
                                    </div>
                                    <div className="border-b py-2 px-2">
                                        <Skeleton className="h-5 w-full" />
                                    </div>
                                    <div className="border-b py-2 px-2">
                                        <Skeleton className="h-5 w-full" />
                                    </div>
                                </React.Fragment>
                            ))}

                            {/* Rows */}
                            {!isLoading && requests.map((request) => (
                                <React.Fragment key={request.uuid}>
                                    <div className="border-b py-2 px-4">
                                        <div className="min-w-0 space-y-0.5">
                                            <div className="flex items-center gap-1">
                                                <TooltipProvider>
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <p className="font-medium truncate text-sm">
                                                                {showName[request.uuid] ? request.requester_name : 'Hidden'}
                                                            </p>
                                                        </TooltipTrigger>
                                                        <TooltipContent>
                                                            {showName[request.uuid] ? request.requester_name : 'Hidden'}
                                                        </TooltipContent>
                                                    </Tooltip>
                                                </TooltipProvider>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => onToggleNameVisibility(request.uuid)}
                                                    className="h-6 w-6"
                                                >
                                                    {showName[request.uuid] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                                </Button>
                                            </div>
                                            <TooltipProvider>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <p className="text-xs text-muted-foreground truncate">
                                                            {showName[request.uuid] ? request.requester_email : 'Hidden'}
                                                        </p>
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                        {showName[request.uuid] ? request.requester_email : 'Hidden'}
                                                    </TooltipContent>
                                                </Tooltip>
                                            </TooltipProvider>
                                        </div>
                                    </div>
                                    <div className="border-b py-2 px-2 flex items-center">
                                        <TooltipProvider>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <p className="truncate text-sm w-full text-center">{request.event?.title || 'N/A'}</p>
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    {request.event?.title || 'N/A'}
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    </div>
                                    <div className="border-b py-2 px-2 font-medium text-green-600 whitespace-nowrap text-sm flex items-center justify-center">
                                        {formatCurrency(request.amount)}
                                    </div>
                                    <div className="border-b py-2 px-2 whitespace-nowrap text-sm flex items-center justify-center">
                                        {format(new Date(request.updatedAt), 'MMM d, yyyy')}
                                    </div>
                                    <div className="border-b py-2 px-2 flex items-center justify-center">
                                        <Badge
                                            variant="outline"
                                            className={cn(
                                                "rounded-full text-xs whitespace-nowrap w-[85px] justify-center",
                                                request.status === 'pending' && "bg-yellow-100 text-yellow-800 border-yellow-200",
                                                request.status === 'approved' && "bg-green-100 text-green-800 border-green-200",
                                                request.status === 'rejected' && "bg-red-100 text-red-800 border-red-200"
                                            )}
                                        >
                                            {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                                        </Badge>
                                    </div>
                                    <div className="border-b py-2 px-2 flex items-center justify-center">
                                        <Button
                                            variant="default"
                                            size="sm"
                                            onClick={() => onViewDetails(request)}
                                            className="h-8 whitespace-nowrap transition-transform hover:scale-105"
                                        >
                                            Details
                                        </Button>
                                    </div>
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Details Modal */}
            <Dialog open={showBursaryModal} onOpenChange={handleCloseModal}>
                <DialogContent className="sm:max-w-[425px] max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Bursary Request Details</DialogTitle>
                    </DialogHeader>
                    {selectedBursary && (
                        <div className="space-y-6">
                            {error && (
                                <Alert variant="destructive" className="mb-4">
                                    <AlertDescription>{error}</AlertDescription>
                                </Alert>
                            )}
                            <div className="space-y-4">
                                <div>
                                    <h3 className="text-sm font-medium text-muted-foreground">Requester Information</h3>
                                    <div className="mt-2 space-y-2">
                                        <div className="flex items-center gap-2">
                                            <p className="text-base font-medium">
                                                {showModalName[selectedBursary.uuid] ? selectedBursary.requester_name : 'Hidden'}
                                            </p>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => onToggleModalNameVisibility(selectedBursary.uuid)}
                                                className="h-6 w-6"
                                            >
                                                {showModalName[selectedBursary.uuid] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                            </Button>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <p className="text-sm text-muted-foreground">
                                                {showModalEmail[selectedBursary.uuid] ? selectedBursary.requester_email : 'Hidden'}
                                            </p>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => onToggleModalEmailVisibility(selectedBursary.uuid)}
                                                className="h-6 w-6"
                                            >
                                                {showModalEmail[selectedBursary.uuid] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                            </Button>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <h3 className="text-sm font-medium text-muted-foreground">Request Details</h3>
                                    <div className="mt-2 space-y-2">
                                        <div className="flex items-center gap-4">
                                            <p className="text-sm text-muted-foreground">Amount Requested:</p>
                                            <p className="font-medium text-green-600">{formatCurrency(selectedBursary.amount)}</p>
                                        </div>
                                        <div className="flex items-center gap-4">
                                            <p className="text-sm text-muted-foreground">Original Price:</p>
                                            <p className="font-medium">{formatCurrency(selectedBursary.ticket_price)}</p>
                                        </div>
                                        <div className="flex items-center gap-4">
                                            <p className="text-sm text-muted-foreground">Request Date:</p>
                                            <p className="font-medium">{format(new Date(selectedBursary.updatedAt), 'MMM d, yyyy')}</p>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <h3 className="text-sm font-medium text-muted-foreground">Event Name</h3>
                                    <div className="mt-2">
                                        <p className="font-medium">{selectedBursary.event?.title || 'N/A'}</p>
                                    </div>
                                </div>

                                <div>
                                    <h3 className="text-sm font-medium text-muted-foreground">Reason for Request</h3>
                                    <div className="mt-2 max-h-[200px] overflow-y-auto">
                                        <p className="text-sm whitespace-pre-wrap">{selectedBursary.message || 'No reason provided'}</p>
                                    </div>
                                </div>

                                <div>
                                    <h3 className="text-sm font-medium text-muted-foreground">Status</h3>
                                    <div className="mt-2">
                                        <Badge
                                            variant="outline"
                                            className={cn(
                                                "rounded-full text-xs whitespace-nowrap w-[85px] justify-center",
                                                (localStatus || selectedBursary?.status) === 'pending' && "bg-yellow-100 text-yellow-800 border-yellow-200",
                                                (localStatus || selectedBursary?.status) === 'approved' && "bg-green-100 text-green-800 border-green-200",
                                                (localStatus || selectedBursary?.status) === 'rejected' && "bg-red-100 text-red-800 border-red-200"
                                            )}
                                        >
                                            {(localStatus || selectedBursary?.status)?.charAt(0).toUpperCase() + (localStatus || selectedBursary?.status)?.slice(1)}
                                        </Badge>
                                    </div>
                                </div>
                            </div>
                            {(!localStatus && selectedBursary?.status === 'pending') && (
                                <div className="flex flex-col gap-4">
                                    <div className="flex flex-col sm:flex-row gap-4">
                                        <Button
                                            variant="outline"
                                            className="flex-1 bg-green-100 text-green-800 hover:bg-green-200 transition-transform hover:scale-105"
                                            onClick={() => handleAccept(selectedBursary.uuid)}
                                        >
                                            <Check className="mr-2 h-4 w-4" />
                                            Accept Request
                                        </Button>
                                        <Button
                                            variant="outline"
                                            className="flex-1 bg-red-100 text-red-800 hover:bg-red-200 transition-transform hover:scale-105"
                                            onClick={() => handleReject(selectedBursary.uuid)}
                                        >
                                            <X className="mr-2 h-4 w-4" />
                                            Reject Request
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default BursaryRequestTable; 